import { useEffect, useState, useRef, useCallback } from 'react';
import { useFileTemporaryLinkMutation } from 'src/data-capture/hooks/useFileTemporaryLinkMutation';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { Logger } from 'src/utils/Logger';
import { parseTimeToSeconds } from 'src/meetings/detail/tabs/transcript/timeUtils';
import type { TranscriptSegment } from 'src/meetings/detail/tabs/transcript/types';

type Props = {
  fileId?: number;
  segments: TranscriptSegment[];
  eventId: string;
};

export const useTranscriptAudio = ({ fileId, segments, eventId }: Props) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [selectedSegmentIndex, setSelectedSegmentIndex] = useState<number | null>(null);
  const [isLoadingAudio, setIsLoadingAudio] = useState(true);
  const [hasAudioError, setHasAudioError] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const hasFetchedRef = useRef(false);
  const { createLink } = useFileTemporaryLinkMutation();

  const findCurrentSegmentIndex = useCallback(
    (currentTimeSeconds: number): number | null => {
      if (segments.length === 0) return null;

      for (let i = 0; i < segments.length; i++) {
        const segment = segments[i];
        const startTime = parseTimeToSeconds(segment.startTime);
        const endTime = parseTimeToSeconds(segment.endTime);

        if (currentTimeSeconds >= startTime && currentTimeSeconds <= endTime) {
          return i;
        }
      }

      const earlierSegments = segments.filter(segment => {
        return parseTimeToSeconds(segment.startTime) < currentTimeSeconds;
      });

      if (earlierSegments.length > 0) {
        return segments.indexOf(earlierSegments[earlierSegments.length - 1]);
      }

      return null;
    },
    [segments],
  );

  useEffect(() => {
    hasFetchedRef.current = false;
  }, [fileId]);

  useEffect(() => {
    const fetchAudioUrl = async () => {
      if (fileId == null || hasAudioError || hasFetchedRef.current) {
        setIsLoadingAudio(false);
        return;
      }

      hasFetchedRef.current = true;
      setIsLoadingAudio(true);

      try {
        const link = await createLink(fileId);
        setAudioUrl(link.url);
      } catch (error) {
        notifyAndLogError(
          'Failed to load audio',
          'An error occurred while trying to load the meeting audio. Please try again later.',
          error,
          { eventId, fileId },
        );
        setHasAudioError(true);
      } finally {
        setIsLoadingAudio(false);
      }
    };

    fetchAudioUrl();
  }, [fileId, eventId]);

  useEffect(() => {
    if (!audioUrl || hasAudioError) {
      return;
    }

    const audio = new Audio(audioUrl);

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setIsLoadingAudio(false);
    };

    const handleTimeUpdate = () => {
      const currentTimeSeconds = audio.currentTime;
      setCurrentTime(currentTimeSeconds);

      const currentSegmentIndex = findCurrentSegmentIndex(currentTimeSeconds);
      if (currentSegmentIndex !== null && currentSegmentIndex !== selectedSegmentIndex) {
        setSelectedSegmentIndex(currentSegmentIndex);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
    };

    const handleError = (e: Event) => {
      Logger.error('Audio error event', e, { eventId, fileId });
      notifyAndLogError(
        'Failed to load audio',
        'The audio file could not be loaded. Please try again later.',
        new Error('Audio element error event'),
        { eventId, fileId, audioUrl },
      );
      setIsLoadingAudio(false);
      setHasAudioError(true);
    };

    const handleStalled = () => {
      Logger.warn('Audio playback stalled', { eventId, fileId });
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    audio.addEventListener('stalled', handleStalled);
    audioRef.current = audio;

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('stalled', handleStalled);
      audio.pause();
      audio.src = '';
      audioRef.current = null;
    };
  }, [audioUrl, hasAudioError, eventId, fileId, findCurrentSegmentIndex]);

  const handlePlayPause = useCallback(() => {
    if (!audioRef.current || hasAudioError) return;

    try {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play().catch(error => {
          notifyAndLogError(
            'Playback failed',
            'Unable to play audio. Your browser may have blocked autoplay.',
            error,
            { eventId },
          );
        });
        setIsPlaying(true);
      }
    } catch (error) {
      notifyAndLogError('Playback error', 'An error occurred during audio playback.', error, {
        eventId,
      });
    }
  }, [isPlaying, hasAudioError, eventId]);

  const handleSeek = useCallback((time: number) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  }, []);

  const handleMuteToggle = useCallback(() => {
    if (!audioRef.current) return;
    audioRef.current.muted = !audioRef.current.muted;
    setIsMuted(!isMuted);
  }, [isMuted]);

  const handleSegmentClick = useCallback(
    (segmentIndex: number) => {
      setSelectedSegmentIndex(segmentIndex);

      if (!audioRef.current || segmentIndex >= segments.length) return;

      const segment = segments[segmentIndex];
      const startTimeSeconds = parseTimeToSeconds(segment.startTime);
      audioRef.current.currentTime = startTimeSeconds;
      setCurrentTime(startTimeSeconds);
    },
    [segments],
  );

  return {
    isPlaying,
    currentTime,
    duration,
    isMuted,
    selectedSegmentIndex,
    isLoadingAudio,
    hasAudioError,
    handlePlayPause,
    handleSeek,
    handleMuteToggle,
    handleSegmentClick,
  };
};
